import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  Pagination,
  message,
  Dropdown,
  Form,
  Select,
  InputNumber,
  Drawer
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  DownOutlined,
  EyeOutlined,
  FilterOutlined,
  ToolOutlined
} from "@ant-design/icons";
import "antd/dist/reset.css";
import {
  generateSlug,
  isValidSlug,
  hasLeadingOrTrailingSpaces,
  isValidSlugLength,
} from "../../utils/SlugValidate";
import { useDebounceSearch } from "../../utils/debounce";
import { render } from "@testing-library/react";
import "../../styles/Courses.css";
import { TABLE_COLUMNS } from "../../constants/Courses";
import CourseApiService from "../../services/CoursesApiService";
import CategoryApiService from "../../services/CategoryApiService";
import AddCourseModal from "../../components/courses/AddCourseModal";
import EditCourseModal from "../../components/courses/EditCourseModal";
import DeleteCourseModal from "../../components/courses/DeleteCourseModal";
import ViewCourseDetailModal from "../../components/courses/ViewCourseDetailModal";
import { useAuth } from "../../contexts/AuthContext";
import { Role } from "../../constants/Role";

const { Search } = Input;

const ManageCourses = () => {
  // Auth context
  const { hasAnyRole } = useAuth();
  
  // Check permissions
  const isAdminDev = hasAnyRole([Role.ADMIN_DEV]);
  const isAdminBusiness = hasAnyRole([Role.ADMIN_BUSINESS]);
  const isStaffCourseManager = hasAnyRole([Role.STAFF_COURSE_MANAGER]);
  
  // Determine if user can perform CUD operations
  const canCreate = isAdminDev || isStaffCourseManager;
  const canUpdate = isAdminDev || isStaffCourseManager;
  const canDelete = isAdminDev || isStaffCourseManager;
  
  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [categories, setCategories] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filterForm] = Form.useForm();
  const [filters, setFilters] = useState({
    category: undefined,
    priceMin: undefined,
    priceMax: undefined,
    status: 'all',
    sort: undefined,
  });

  const sortOptions = [
    { label: "Name (A-Z)", value: "name" },
    { label: "Name (Z-A)", value: "-name" },
    { label: "Price Ascending", value: "price" },
    { label: "Price Descending", value: "-price" },
    { label: "Newest", value: "-createdAt" },
    { label: "Oldest", value: "createdAt" },
    { label: "Top Selling", value: "-purchaseCount" },
  ];
  const statusOptions = [
    { label: "All", value: undefined },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  // Helper to get latest pagination state for debounced search
  const getLatestPagination = useCallback(() => pagination, [pagination]);

  // Use the new debounce hook
  const { debouncedSearch, clearSearchTimeout } = useDebounceSearch((value, latestParams) => {
    const trimmedValue = value.trim();
    if (trimmedValue !== value) {
      message.warning("Search term has been trimmed");
    }
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchCourses({ search: trimmedValue, page: 1, pageSize: latestParams.pageSize });
  }, 500, getLatestPagination);

  const fetchCourses = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const courseApi = CourseApiService;
      const mergedFilters = {
        ...filters,
        ...params,
      };
      const response = await courseApi.getCourses({
        page: mergedFilters.page,
        pageSize: mergedFilters.pageSize,
        search: mergedFilters.search,
        sort: mergedFilters.sort,
        category: mergedFilters.category,
        priceMin: mergedFilters.priceMin,
        priceMax: mergedFilters.priceMax,
        status: mergedFilters.status,
      });
      
      setData(response.courses.map(course => ({
        ...course,
        pagination: {
          current: response.pagination.currentPage,
          pageSize: response.pagination.itemsPerPage
        }
      })));
      setPagination(prev => ({
        ...prev,
        total: response.pagination.totalItems,
        current: response.pagination.currentPage,
        pageSize: response.pagination.itemsPerPage
      }));
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 401:
            message.error("You must be logged in to view courses");
            break;
          case 403:
            message.error("You don't have permission to view courses");
            break;
          default:
            message.error(error.response.data?.message || "Failed to fetch courses");
        }
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error("Failed to fetch courses. Please try again later");
      }
    } finally {
      setLoading(false);
    }
  }, [setData, setPagination, setLoading, filters]);

  const handleSearch = useCallback((value) => {
    clearSearchTimeout(); // Clear any pending debounced search
    const trimmedValue = value.trim();
    if (trimmedValue !== value) {
      message.warning("Search term has been trimmed");
    }
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchCourses({ search: trimmedValue, page: 1, pageSize: pagination.pageSize });
  }, [clearSearchTimeout, fetchCourses, pagination.pageSize]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchText(value);
    // Only trigger debounced search when typing
    debouncedSearch(value);
  }, [debouncedSearch]);

  const handleTableChange = useCallback((pag) => {
    setPagination(pag);
    fetchCourses({
      page: pag.current,
      pageSize: pag.pageSize,
      search: searchText
    });
  }, [fetchCourses, searchText]);

  const fetchCategories = useCallback(async () => {
    try {
      let res;
      if (isAdminDev || isAdminBusiness) {
        res = await CategoryApiService.getCategories({
          page: 1,
          limit: 100,
          search: ''
        });
      } else {
        // Staff chỉ lấy các category được assign
        res = await CategoryApiService.getCategories({
          page: 1,
          limit: 100,
          search: '',
          assignedOnly: true
        });
      }
      setCategories(res.categories || []);
    } catch (error) {
      setCategories([]);
      message.error("Failed to fetch categories");
    }
  }, [isAdminDev, isAdminBusiness]);

   useEffect(() => {
    fetchCourses({ page: 1, pageSize: 10, search: "" });
    fetchCategories();
  }, [fetchCourses, fetchCategories]);

// Modal handlers
  const handleAdd = useCallback(() => {
    form.resetFields();
    setIsAddModalOpen(true);
  }, [form]);

  const handleEdit = useCallback(async (course) => {
    try {
      setLoading(true);
      const courseApi = CourseApiService;
      const response = await courseApi.getCourseById(course._id);
      const courseData = response.data.course;
      
      setSelectedCourse(courseData);
      form.setFieldsValue({
        name: courseData.name,
        slug: courseData.slug,
        description: courseData.description,
        category: courseData.category._id,
        price: courseData.price,
        status: courseData.isActive ? 'active' : 'inactive',
        author: courseData.author,
        imageUrl: courseData.imageUrl
      });
      setIsEditModalOpen(true);
    } catch (error) {
      console.error('Error fetching course details:', error);
      message.error("Failed to fetch course details");
    } finally {
      setLoading(false);
    }
  }, [form]);

  const handleDelete = useCallback((course) => {
    setSelectedCourse(course);
    setIsDeleteModalOpen(true);
  }, []);

  const handleView = useCallback(async (course) => {
    try {
      setLoading(true);
      const courseApi = CourseApiService;
      const response = await courseApi.getCourseById(course._id);
      console.log('Course details:', response); // For debugging
      setSelectedCourse(response.data.course);
      setIsViewModalOpen(true);
    } catch (error) {
      console.error('Error fetching course details:', error);
      message.error("Failed to fetch course details");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpenFilterDrawer = () => {
    filterForm.setFieldsValue({ ...filters, status: filters.status || 'all' });
    setIsFilterDrawerOpen(true);
  };
  const handleFilterOk = () => {
    filterForm.validateFields().then(values => {
      setFilters(values);
      setIsFilterDrawerOpen(false);
      setPagination(prev => ({ ...prev, current: 1 }));
      fetchCourses({
        ...values,
        page: 1,
        pageSize: pagination.pageSize,
        search: values.search !== undefined ? values.search : searchText,
        sort: values.sort,
        category: Array.isArray(values.category) ? values.category : values.category ? [values.category] : undefined,
        priceMin: values.priceMin,
        priceMax: values.priceMax,
        status: values.status,
      });
    });
  };
  const handleFilterCancel = () => {
    setIsFilterDrawerOpen(false);
    filterForm.setFieldsValue(filters);
  };
  const handleClearFilter = () => {
    setFilters({ category: undefined, priceMin: undefined, priceMax: undefined, status: 'all', sort: undefined });
    filterForm.resetFields();
    setIsFilterDrawerOpen(false);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchCourses({ page: 1, pageSize: pagination.pageSize, search: searchText, status: 'all' });
  };

  // CRUD handlers
  const handleAddOk = useCallback(async (formData) => {
    try {
      setLoading(true);
      const courseApi = CourseApiService;
      const response = await courseApi.createCourse(formData);
      
      if (response.status === 'success') {
        message.success("Course added successfully");
        setIsAddModalOpen(false);
        fetchCourses();
      } else {
        throw new Error(response.message || 'Failed to add course');
      }
    } catch (error) {
      console.error('Error adding course:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Hiển thị lỗi validation chi tiết nếu có
        if (errorData.errors) {
          Object.entries(errorData.errors).forEach(([field, msg]) => {
            message.error(`${field.charAt(0).toUpperCase() + field.slice(1)}: ${msg}`);
          });
        } 
        // Hiển thị lỗi từ server
        else if (errorData.message) {
          message.error(errorData.message);
        }
        // Hiển thị lỗi theo status code
        else {
          switch (error.response.status) {
            case 400:
              message.error("Invalid course data. Please check your input.");
              break;
            case 401:
              message.error("You must be logged in to add a course");
              break;
            case 403:
              message.error("You don't have permission to add courses");
              break;
            case 409:
              message.error("A course with this name or slug already exists");
              break;
            case 413:
              message.error("Image file size is too large. Maximum size is 5MB");
              break;
            case 415:
              message.error("Invalid image format. Supported formats: JPG, PNG, JPEG");
              break;
            default:
              message.error("Failed to add course. Please try again");
          }
        }
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error("An unexpected error occurred. Please try again");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchCourses]);

  const handleEditOk = useCallback(async (formData) => {
    try {
      setLoading(true);
      const courseApi = CourseApiService;
      
      const response = await courseApi.updateCourse(selectedCourse._id, formData);
      
      if (response.status === 'success') {
        message.success("Course updated successfully");
        setIsEditModalOpen(false);
        setSelectedCourse(null);
        form.resetFields();
        fetchCourses({
          page: pagination.current,
          pageSize: pagination.pageSize,
          search: searchText,
          sort: filters.sort,
          category: filters.category,
          priceMin: filters.priceMin,
          priceMax: filters.priceMax,
          status: filters.status,
        });
      } else {
        throw new Error(response.message || 'Failed to update course');
      }
    } catch (error) {
      console.error('Error updating course:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Hiển thị lỗi validation chi tiết nếu có
        if (errorData.errors) {
          Object.entries(errorData.errors).forEach(([field, msg]) => {
            message.error(`${field.charAt(0).toUpperCase() + field.slice(1)}: ${msg}`);
          });
        } 
        // Hiển thị lỗi từ server
        else if (errorData.message) {
          message.error(errorData.message);
        }
        // Hiển thị lỗi theo status code
        else {
          switch (error.response.status) {
            case 400:
              message.error("Invalid course data. Please check your input.");
              break;
            case 401:
              message.error("You must be logged in to update this course");
              break;
            case 403:
              message.error("You don't have permission to update this course");
              break;
            case 404:
              message.error("Course not found or has been deleted");
              break;
            case 409:
              message.error("A course with this name already exists");
              break;
            case 413:
              message.error("Image file size is too large. Maximum size is 5MB");
              break;
            case 415:
              message.error("Invalid image format. Supported formats: JPG, PNG, JPEG");
              break;
            default:
              message.error("Failed to update course. Please try again");
          }
        }
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error("An unexpected error occurred. Please try again");
      }
    } finally {
      setLoading(false);
    }
  }, [selectedCourse, form, fetchCourses, pagination.current, pagination.pageSize, searchText, filters]);

  const handleDeleteOk = useCallback(async () => {
    if (!selectedCourse) {
      message.error("No course selected for deletion");
      return;
    }

    setDeleteLoading(true);
    try {
      const courseApi = CourseApiService;
      const res = await courseApi.deleteCourse(selectedCourse._id);
      // Kiểm tra message trả về từ backend
      if (res && res.deletionType === 'soft') {
        message.warning("Course cannot be deleted because it contains sections, lessons, orders, or ratings. The course has been deactivated instead.");
      } else {
        message.success("Course deleted successfully");
      }
      setIsDeleteModalOpen(false);
      setSelectedCourse(null);
      fetchCourses({
        page: pagination.current,
        pageSize: pagination.pageSize,
        search: searchText,
        sort: filters.sort,
        category: filters.category,
        priceMin: filters.priceMin,
        priceMax: filters.priceMax,
        status: filters.status,
      });
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 401:
            message.error("You must be logged in to delete this course");
            break;
          case 403:
            message.error("You don't have permission to delete this course");
            break;
          case 404:
            message.error("Course not found or has already been deleted");
            break;
          case 409:
            message.error("Cannot delete course - it has enrolled students or active lessons");
            break;
          default:
            message.error(error.response.data?.message || "Failed to delete course");
        }
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error("Failed to delete course. Please try again later");
      }
    } finally {
      setDeleteLoading(false);
    }
  }, [selectedCourse, fetchCourses, pagination.current, pagination.pageSize, searchText, filters]);

  const handleModalCancel = useCallback(() => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsViewModalOpen(false);
    setSelectedCourse(null);
    form.resetFields();
  }, [form]);

  // Memoized values
  const actionColumns = useMemo(
    () => [
      {
        title: "Operation",
        key: "operation",
        align: "center",
        render: (text, record) => {
          const menuItems = [
            {
              key: "course-builder",
              icon: <ToolOutlined />,
              label: "Course Builder",
              onClick: () => navigate(`/course-builder/${record._id}`),
            },
            { type: 'divider' },
            {
              key: "view",
              icon: <EyeOutlined />,
              label: "View",
              onClick: () => handleView(record),
            },
          ];

          // Only add edit and delete options if user has permission
          if (canUpdate) {
            menuItems.push({
              key: "edit",
              icon: <EditOutlined />,
              label: "Edit",
              onClick: () => handleEdit(record),
            });
          }
          
          if (canDelete) {
            menuItems.push({
              key: "delete",
              icon: <DeleteOutlined />,
              label: "Delete",
              onClick: () => handleDelete(record),
            });
          }

          return (
            <Dropdown
              menu={{
                items: menuItems,
              }}
              trigger={["click"]}
            >
              <Button>
                Actions <DownOutlined />
              </Button>
            </Dropdown>
          );
        },
      },
    ],
    [navigate, handleView, handleEdit, handleDelete, canUpdate, canDelete]
  );

  const handlePriceKeyPress = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      charCode !== 46 && // dấu chấm
      charCode > 31 &&
      (charCode < 48 || charCode > 57)
    ) {
      e.preventDefault();
      message.error('Please enter numbers only. Letters and special characters are not allowed in price field.');
      return false;
    }
    return true;
  };

  return (
    <div className="courses-container">
      <h1 className="admin-title">Courses Management</h1>
      <div className="top-bar-courses">
        <Search
          placeholder="Search by course name..."
          allowClear
          value={searchText}
          onChange={handleSearchChange}
          onSearch={handleSearch}
          style={{ maxWidth: 500 }}
        />
        <div className="top-bar-buttons-courses">
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              className="add-btnw"
            >
              Add Course
            </Button>
          )}
          <Button
            style={{ marginLeft: 8 }}
            icon={<FilterOutlined />}
            onClick={handleOpenFilterDrawer}
          >
            Advanced Filter
          </Button>
        </div>
      </div>
      <Table
        className="striped-table"
        columns={[
          ...TABLE_COLUMNS.map(col => {
            if (col.key === 'slug') {
              return {
                ...col,
                render: (text, record) => (
                  <a onClick={(e) => {
                    e.preventDefault();
                    handleView(record);
                  }} href="#">
                    {text}
                  </a>
                )
              };
            }
            return col;
          }),
          ...actionColumns
        ]}
        dataSource={data}
        loading={loading}
        bordered
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} courses`,
        }}
        onChange={handleTableChange}
      />

      <AddCourseModal
        visible={isAddModalOpen}
        onCancel={handleModalCancel}
        onOk={handleAddOk}
        categories={categories}
        form={form}
      />

      <EditCourseModal
        visible={isEditModalOpen}
        onCancel={handleModalCancel}
        onOk={handleEditOk}
        categories={categories}
        form={form}
        initialValues={selectedCourse}
      />

      <DeleteCourseModal
        visible={isDeleteModalOpen}
        onCancel={handleModalCancel}
        onOk={handleDeleteOk}
        courseName={selectedCourse?.name}
        isLoading={deleteLoading}
      />

      <ViewCourseDetailModal
        visible={isViewModalOpen}
        onCancel={handleModalCancel}
        course={selectedCourse}
      />

      <Drawer
        title={<span style={{ fontWeight: 700, fontSize: 22, color: '#283593', letterSpacing: 0.5 }}>Advanced Filter</span>}
        placement="right"
        width={420}
        open={isFilterDrawerOpen}
        onClose={handleFilterCancel}
        bodyStyle={{ padding: 28, background: '#f9f9f9' }}
        style={{ borderRadius: 0, top: 0 }}
        footer={null}
      >
        <Form form={filterForm} layout="vertical">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
            {/* <div style={{ flex: 1, minWidth: 220 }}>
              <Form.Item label="Course Name" name="search">
                <Input placeholder="Enter course name..." style={{ borderRadius: 8 }} />
              </Form.Item>
            </div> */}
            <div style={{ flex: 1, minWidth: 220 }}>
              <Form.Item label="Category" name="category">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select category(s)"
                  style={{ borderRadius: 8 }}
                >
                  {categories.map(cat => (
                    <Select.Option key={cat._id} value={cat._id}>{cat.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Form.Item label="Price Range" style={{ marginBottom: 0 }}>
                <Input.Group compact>
                  <Form.Item name="priceMin" noStyle>
                    <InputNumber min={0} placeholder="Min price" style={{ width: 100, borderRadius: 8 }} onKeyPress={handlePriceKeyPress} />
                  </Form.Item>
                  <span style={{ margin: '0 8px' }}>-</span>
                  <Form.Item name="priceMax" noStyle>
                    <InputNumber min={0} placeholder="Max price" style={{ width: 100, borderRadius: 8 }} onKeyPress={handlePriceKeyPress} />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Form.Item label="Status" name="status">
                <Select allowClear placeholder="Select status" style={{ borderRadius: 8 }}>
                  <Select.Option value="all">All</Select.Option>
                  <Select.Option value="active">Active</Select.Option>
                  <Select.Option value="inactive">Inactive</Select.Option>
                </Select>
              </Form.Item>
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Form.Item label="Sort by" name="sort">
                <Select allowClear placeholder="Select sort field" style={{ borderRadius: 8 }}>
                  {sortOptions.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 32 }}>
            <Button onClick={handleClearFilter} style={{ borderRadius: 8 }}>Clear Filter</Button>
            <Button onClick={handleFilterCancel} style={{ borderRadius: 8 }}>Cancel</Button>
            <Button type="primary" onClick={handleFilterOk} style={{ borderRadius: 8, minWidth: 90 }}>Apply</Button>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default ManageCourses;